import i18n from "i18next";
import Backend from "i18next-chained-backend";
import HttpApi, { BackendOptions as HttpBackendOptions } from "i18next-http-backend";
import LocalStorageBackend from "i18next-localstorage-backend";
import { initReactI18next } from "react-i18next";
import isEqual from "lodash-es/isEqual";

import { formatDistance, parseISO } from "date-fns";
import { getTranslation } from "api";
import { getUserLang } from "utils";

import fr from "date-fns/locale/fr";
import de from "date-fns/locale/de";
import en from "date-fns/locale/en-US";
import { AxiosError } from "axios";

const backendParam = {
  backends: [LocalStorageBackend, HttpApi],
  backendOptions: [
    {
      prefix: "i18next_res_",
      // en production, on met en cache les traductions pour la journée
      expirationTime: import.meta.env.PROD ? 1 * 24 * 60 * 60 * 1000 : 15000
    },
    {
      // Définition de l'url du webservice qui permet d'aller chercher les traductions
      loadPath: "{{lng}}/{{ns}}",

      // Chemin où poster les ressouces manquantes
      // Inutilisé pour le moment
      addPath: "add/{{lng}}/{{ns}}",

      // Autorise la chargement de plusieurs namesapces en parrallèle
      // On en a un seul donc false suffit
      allowMultiLoading: false,

      // Définition de la méthode qui charge les ressources statiques
      request: function(options, url, payload, callback) {
        getTranslation(url)
          .then(res => {
            const result = { status: res.status, data: res.data };
            // callback attend un objet json valide et le status de la réponse
            callback(null, result as any);
            // callback(res.request.response, { status: res.status });
          })
          .catch((err: AxiosError<any>) => {
            callback(err, { status: err.response?.status ?? 500, data: "" });
            /* rien a faire en cas d'erreur */
          });
      }
    } as HttpBackendOptions
  ]
};

export function initI18n() {
  i18n
    .use(Backend)
    .use(initReactI18next)
    .init({
      lng: "fr",
      fallbackLng: "fr",
      ns: ["tCb", "cbCommun"],
      defaultNS: "cbCommun",
      debug: false,
      interpolation: {
        escapeValue: false,
        format: (value, format) => {
          switch (format) {
            case "number": {
              if (typeof value === "number") {
                return formatNumber(value);
              }

              return value;
            }

            case "date":
              return formatDate(value);

            case "dateRelative":
              return formatDateRelative(value);

            default:
              return value;
          }
        }
      },
      pluralSeparator: "#", // on redéfinit cette constante afin de garder le "_" comme séparateur dans notre clé
      contextSeparator: ";", // on redéfinit cette constante afin de garder le "_" comme séparateur dans notre clé,
      backend: backendParam,
      saveMissing: import.meta.env.DEV,
      missingKeyHandler: (lng, ns, key, fallbackValue) => {
        if (key !== fallbackValue) {
          // api.post(`/translation/new`, {
          //   key,
          //   value: fallbackValue
          // });
        }
      }
    });
}

export default i18n;

/**
 * Permet de formatter une date en fonction de la locale courante.
 *
 * @export
 * @param {(Date | string | undefined | null)} date
 * @returns
 */
export function formatDate(
  date: Date | string | undefined | null,
  options?: Intl.DateTimeFormatOptions
) {
  if (!date) {
    return null;
  }

  const parsedDate = date instanceof Date ? date : parseISO(date);

  const dateFormat = getDateFormat(options);
  return dateFormat.format(parsedDate);
}

export function formatDateRelative(date: Date | string | undefined | null, includeSeconds = false) {
  if (!date) {
    return null;
  }

  const parsedDate = date instanceof Date ? date : parseISO(date);

  const locale = getUserLocaleDateFns();
  return formatDistance(parsedDate, Date.now(), { includeSeconds, locale });
}

/**
 * Permet de formatter un nombre vers une représentation en string correcte en fonction de la locale utilisateur
 *
 * @export
 * @param {number} nb nombre à formatter
 * @returns retourne
 */
export function formatNumber(nb: number | null, options?: Intl.NumberFormatOptions) {
  if (nb === null) {
    return "";
  }

  let currentFormatter = getNumberFormat(options);
  return currentFormatter.format(nb);
}

export function parseLocaleNumber(stringNumber: string | null, options?: Intl.NumberFormatOptions) {
  if (stringNumber === null) {
    return null;
  }
  let numberFormat = getNumberFormat(options);

  var thousandSeparator = numberFormat.format(1111).replace(/1/g, "");
  var decimalSeparator = numberFormat.format(1.1).replace(/1/g, "");

  return parseFloat(
    stringNumber
      .replace(new RegExp("\\" + thousandSeparator, "g"), "")
      .replace(new RegExp("\\" + decimalSeparator), ".")
  );
}

export const LOCALE = {
  fr,
  de,
  en
};

export function getUserLocaleDateFns() {
  const lang = getUserLang();

  return LOCALE[lang];
}

let dateFormat: Intl.DateTimeFormat;
export function getDateFormat(options?: Intl.DateTimeFormatOptions) {
  if (dateFormat === undefined || (options && !isEqual(dateFormat.resolvedOptions(), options))) {
    dateFormat = new Intl.DateTimeFormat(getUserLang(), options);
  }

  return dateFormat;
}

let numberFormat: Intl.NumberFormat;
export function getNumberFormat(options?: Intl.NumberFormatOptions): Intl.NumberFormat {
  if (
    numberFormat === undefined ||
    (options && !isEqual(numberFormat.resolvedOptions(), options))
  ) {
    numberFormat = new Intl.NumberFormat(getUserLang(), {
      maximumFractionDigits: 2,
      ...options
    });
  }

  return numberFormat;
}

let collator: Intl.Collator;
export function getCollator(options?: Intl.CollatorOptions): Intl.Collator {
  if (collator === undefined || (options && !isEqual(collator.resolvedOptions(), options))) {
    collator = new Intl.Collator(getUserLang(), options);
  }

  return collator;
}
